import { useState } from 'react';
import { Download, Loader2, X } from 'lucide-react';
import { warehouseApi, apiError } from '../shared/warehouseApi';
import { downloadReturnBatch } from '../../../shared/inventory/returnBatchDownload';

export function ReturnBatchModal({ packages, onSaved, onClose }) {
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [saved, setSaved] = useState(null);
  const [error, setError] = useState('');
  const stores = [...new Set(packages.map(pkg => pkg.store_user?.id).filter(Boolean))];
  const oneStore = stores.length === 1 && packages.every(pkg => pkg.store_user?.id === stores[0]);
  const providers = [...new Set(packages.map(pkg => pkg.store_user?.provider_id).filter(Boolean))];
  const oneProvider = stores.length > 1 && providers.length === 1
    && packages.every(pkg => pkg.store_user?.id && pkg.store_user?.provider_id === providers[0]);
  const validScope = oneStore || oneProvider;
  const rows = saved?.items || packages;

  const confirm = async () => {
    setSaving(true);
    setError('');
    try {
      const { data } = await warehouseApi.post('/api/admin/return-batches', {
        package_ids: packages.map(pkg => pkg.id),
      });
      setSaved(data);
      onSaved();
    } catch (err) {
      setError(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const download = async () => {
    setDownloading(true);
    setError('');
    try {
      await downloadReturnBatch(saved.id);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true" aria-label="Preparar devolución">
      <div className="flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b px-5 py-4">
          <div>
            <h2 className="text-lg font-bold text-gray-800">Lote de devolución</h2>
            <p className="text-sm text-gray-500">{rows.length} paquete(s) · {saved?.store_name || (oneProvider ? `Proveedor #${providers[0]} · ${stores.length} tiendas` : packages[0]?.store_user?.username || 'Tienda')}</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Cerrar" className="rounded-lg p-2 hover:bg-gray-100"><X size={20} /></button>
        </div>
        <div className="overflow-auto p-5">
          {!validScope && <p className="mb-4 rounded-lg bg-amber-50 p-3 text-sm text-amber-800">Selecciona paquetes de una tienda o de tiendas del mismo proveedor.</p>}
          {saved && <p className="mb-4 rounded-lg bg-green-50 p-3 text-sm text-green-800">Lote #{saved.id} guardado. {saved.package_count} paquetes en Devuelto; {saved.handed_over_count} con entrega a tienda reportada por conductor.</p>}
          {error && <p role="alert" className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</p>}
          <div className="space-y-2 sm:hidden">{rows.map(pkg => <article key={pkg.id} className="rounded-lg border p-3 text-sm">
            <strong>#{pkg.delivery_point_id || pkg.delivery_point_provincia_id}</strong>
            <p className="text-gray-500">{pkg.store_name || pkg.store_user?.username || 'Sin tienda'}</p>
            <p className="break-words">{pkg.recipient_name || 'Sin destinatario'} · {pkg.recipient_phone || 'Sin teléfono'}</p>
            <p className="break-words text-gray-500">{pkg.driver_notes || 'Sin notas'}</p>
            <p className="text-gray-500">{pkg.delivery_status || 'Sin estado'}</p>
          </article>)}</div>
          <div className="hidden overflow-x-auto sm:block"><table className="w-full min-w-[880px] text-left text-sm">
            <thead className="border-b bg-gray-50 text-xs uppercase text-gray-500"><tr>
              <th className="p-3">Entrega</th><th className="p-3">Tienda</th><th className="p-3">Destinatario</th><th className="p-3">Teléfono</th>
              <th className="p-3">Notas del conductor</th><th className="p-3">Estado de entrega</th>
            </tr></thead>
            <tbody className="divide-y">
              {rows.map(pkg => <tr key={pkg.id}>
                <td className="p-3">#{pkg.delivery_point_id || pkg.delivery_point_provincia_id} {(pkg.is_provincia || pkg.delivery_type === 'provincia') ? '(Provincia)' : ''}</td>
                <td className="p-3">{pkg.store_name || pkg.store_user?.username || '—'}</td>
                <td className="p-3">{pkg.recipient_name || '—'}</td>
                <td className="p-3">{pkg.recipient_phone || '—'}</td>
                <td className="max-w-xs whitespace-pre-wrap p-3">{pkg.driver_notes || '—'}</td>
                <td className="p-3">{pkg.delivery_status || '—'}</td>
              </tr>)}
            </tbody>
          </table></div>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 border-t px-5 py-4">
          <button type="button" onClick={download} disabled={!saved || downloading} title={saved ? 'Descargar Excel del lote' : 'Confirma el lote antes de descargar'} aria-label="Descargar Excel del lote" className="inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-semibold text-brand-primary disabled:opacity-40">
            {downloading ? <Loader2 className="animate-spin" size={18} /> : <Download size={18} />} Descargar Excel
          </button>
          {saved ? <button type="button" onClick={onClose} className="rounded-lg bg-brand-primary px-4 py-2 text-sm font-bold text-white">Cerrar</button>
            : <button type="button" onClick={confirm} disabled={!validScope || saving} className="inline-flex items-center gap-2 rounded-lg bg-brand-primary px-4 py-2 text-sm font-bold text-white disabled:opacity-50">
              {saving && <Loader2 className="animate-spin" size={16} />} Confirmar y marcar Devuelto
            </button>}
        </div>
      </div>
    </div>
  );
}
